import { generateClient } from 'aws-amplify/api';
import type {
  Application,
  ApplicationFilter,
  ApplicationNote,
  ApplicationDocument,
  ChildApplication,
  SubmitApplicationInput,
  UpdateApplicationStatusInput,
  AssignApplicationInput,
  ScheduleInterviewInput,
  ScheduleVisitInput,
  UpdateChildApplicationInput,
  UpdateEnrollmentProgressInput,
  UpsertEnrollmentStepDataInput,
  UpdateMedicalRecordInput,
  CompleteEnrollmentInput,
  CreateNoteInput,
  UpdateNoteInput,
  DeleteNoteInput,
  UploadDocumentInput,
  TimelineEvent,
  ApplicationQuickView,
  CalendarEvent,
  CalendarEventFilter,
  UserPreferences,
  UpdateUserPreferencesInput,
} from '@triedge/admissions-domain';
import { askAdmissionsAIMutation } from '../adapters/graphql-operations';
import {
  callAdmissionsTransportMutation,
  callAdmissionsTransportQuery,
} from '../adapters/domain-router-transport';
import { isAmplifyConfigured } from '../adapters/amplify-client';

export interface ApplicationConnection {
  items: Application[];
  totalCount: number;
  nextToken: string | null;
}

export interface TimelineConnection {
  items: TimelineEvent[];
  totalCount: number;
  nextToken: string | null;
}

export interface AdmissionsAIContext {
  status?: string[];
  grade?: string[];
  assignedTo?: string;
  search?: string;
  selectedIds?: string[];
  currentView?: 'list' | 'kanban' | 'calendar';
  personaRole?: 'staff' | 'nurse' | 'parent' | 'admin' | 'owner' | 'editor' | 'viewer';
  personaName?: string;
  activeApplicationId?: string;
  activeApplicationCode?: string;
  activeStudentId?: string;
  activeStudentName?: string;
  faqReference?: {
    title: string;
    content: string;
    source?: string;
  };
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AdmissionsCitation {
  table: 'Applications' | 'Timeline' | 'CalendarEvents' | 'FAQ';
  key: {
    id?: string;
    applicationId?: string;
    eventKey?: string;
  };
  fieldPath: string;
  preview: string;
  applicationCode?: string;
}

export interface AdmissionsAIResponse {
  answer: string;
  citations: AdmissionsCitation[];
  actions?: Record<string, unknown>[];
  module?: string;
  debug?: Record<string, unknown> | null;
  presentation?: Record<string, unknown> | null;
}

type AdmissionsAIResponseGraphQL = {
  answer: string;
  citations: AdmissionsCitation[];
  actions?: unknown;
  module?: string | null;
  debug?: unknown;
  presentation?: unknown;
};

const toAwsJsonOrNull = (value: unknown | null | undefined): string | null => (
  value == null ? null : JSON.stringify(value)
);
const parseAwsJsonObject = (value: unknown): Record<string, unknown> | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
    } catch {
      return undefined;
    }
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return undefined;
};

export async function submitApplication(input: SubmitApplicationInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('submitApplication', input);
}

export async function listApplications(options: {
  filter?: ApplicationFilter;
  limit?: number;
  nextToken?: string | null;
} = {}): Promise<ApplicationConnection> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  const limit = typeof options.limit === 'number' ? options.limit : 50;
  const payload = await callAdmissionsTransportQuery('listApplications', {
    filter: options.filter ?? null,
    limit,
    nextToken: options.nextToken ?? null,
  });
  return {
    items: payload.items ?? [],
    totalCount: payload.totalCount ?? payload.items?.length ?? 0,
    nextToken: payload.nextToken ?? null,
  };
}

export async function getApplication(id: string): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportQuery('getApplication', { id });
}

export async function lookupApplication(
  code: string,
  familyName: string
): Promise<{ found: boolean; application: Application | null }> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportQuery('lookupApplication', { code, familyName });
}

export async function updateApplicationStatus(input: UpdateApplicationStatusInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('updateApplicationStatus', input);
}

export async function assignApplication(input: AssignApplicationInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('assignApplication', input);
}

export async function scheduleInterview(input: ScheduleInterviewInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('scheduleInterview', input);
}

export async function scheduleVisit(input: ScheduleVisitInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('scheduleVisit', input);
}

export async function updateChildApplication(input: UpdateChildApplicationInput): Promise<ChildApplication> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('updateChildApplication', input);
}

export async function updateEnrollmentProgress(input: UpdateEnrollmentProgressInput): Promise<ChildApplication> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('updateEnrollmentProgress', input);
}

export async function upsertEnrollmentStepData(input: UpsertEnrollmentStepDataInput): Promise<ChildApplication> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('upsertEnrollmentStepData', input);
}

export async function updateMedicalRecord(input: UpdateMedicalRecordInput): Promise<ChildApplication> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('updateMedicalRecord', input);
}

export async function completeEnrollment(input: CompleteEnrollmentInput): Promise<Application> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('completeEnrollment', input);
}

export async function addNote(
  input: {
    applicationId: string;
    note: Omit<CreateNoteInput, 'applicationId' | 'authorName'> & {
      authorId?: string;
      authorRole?: string;
      authorName?: string;
    };
  }
): Promise<ApplicationNote> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('addNote', input);
}

export async function updateNote(input: UpdateNoteInput): Promise<ApplicationNote> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('updateNote', input);
}

export async function deleteNote(input: DeleteNoteInput): Promise<boolean> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('deleteNote', input);
}

export async function addDocument(
  input: {
    applicationId: string;
    document: Omit<UploadDocumentInput, 'applicationId'> & { uploadedBy?: string };
  }
): Promise<ApplicationDocument> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('addDocument', input);
}

export async function sendApplicationMessage(input: {
  applicationId: string;
  subject: string;
  message: string;
}): Promise<TimelineEvent> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportMutation('sendApplicationMessage', input);
}

export async function listTimelineEvents(options: {
  filter?: { applicationId?: string; eventTypes?: string[]; majorOnly?: boolean };
  limit?: number;
  nextToken?: string | null;
} = {}): Promise<TimelineConnection> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  const payload = await callAdmissionsTransportQuery('listTimelineEvents', {
    ...(options.filter ? { filter: options.filter } : {}),
    ...(typeof options.limit === 'number' ? { limit: options.limit } : {}),
    ...(options.nextToken ? { nextToken: options.nextToken } : {}),
  });

  return {
    items: payload.items ?? [],
    totalCount: payload.totalCount ?? 0,
    nextToken: payload.nextToken ?? null,
  };
}

export async function getApplicationQuickView(applicationId: string): Promise<ApplicationQuickView> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return callAdmissionsTransportQuery('getApplicationQuickView', { applicationId });
}

export async function listCalendarEvents(options: { filter?: CalendarEventFilter } = {}): Promise<CalendarEvent[]> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  return (await callAdmissionsTransportQuery('listCalendarEvents', {
    filter: options.filter ?? null,
  })) ?? [];
}

export async function getUserPreferences(): Promise<UserPreferences> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  const preferences = await callAdmissionsTransportQuery('getUserPreferences', {});
  return {
    ...preferences,
    filters: parseAwsJsonObject(preferences.filters),
  };
}

export async function updateUserPreferences(input: UpdateUserPreferencesInput): Promise<UserPreferences> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  const preferences = await callAdmissionsTransportMutation('updateUserPreferences', input);
  return {
    ...preferences,
    filters: parseAwsJsonObject(preferences.filters),
  };
}

export async function askAdmissionsAI(
  question: string,
  conversationHistory: ConversationMessage[] = [],
  context?: AdmissionsAIContext,
  model?: string
): Promise<AdmissionsAIResponse> {
  if (!isAmplifyConfigured()) {
    throw new Error('Bridge not configured');
  }
  const client = generateClient();
  const variables = {
    question,
    conversationHistory: toAwsJsonOrNull(conversationHistory),
    context: toAwsJsonOrNull(context ?? null),
    model: model ?? null,
  };

  const response = await client.graphql({
    query: askAdmissionsAIMutation,
    variables,
  });
  const payload = (response as { data: { askAdmissionsAI: AdmissionsAIResponseGraphQL } }).data.askAdmissionsAI;
  const result: AdmissionsAIResponse = {
    answer: payload.answer,
    citations: payload.citations ?? [],
    actions: Array.isArray(payload.actions) ? payload.actions as Record<string, unknown>[] : [],
    debug: parseAwsJsonObject(payload.debug) ?? null,
    presentation: parseAwsJsonObject(payload.presentation) ?? null,
  };
  if (typeof payload.module === 'string') {
    result.module = payload.module;
  }
  return result;
}
